/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/

import React, { useState, useEffect } from 'react';
import { BuildingType } from '../../types';
import { BUILDINGS } from '../../constants';
import { playUiSfx } from '../../utils/uiAudio';
import { tooltipContentService } from '../../services/tooltipContentService';

// Helper functions for detailed tooltips
const getBuildingCategory = (category: string): string => {
  const categories: Record<string, string> = {
    'Infrastructure': '🏗️ Infrastructure',
    'Governance': '🏛️ Governance',
    'Security': '🛡️ Security',
    'Legal': '⚖️ Legal',
    'Ops': '⚙️ Operations',
    'Cyber': '💻 Cyber',
    'ZeroTrust': '🔐 Zero Trust',
    'EnhancedSecurity': '🔒 Advanced Security',
    'EnhancedCompliance': '📋 Enhanced Compliance'
  };
  return categories[category] || category;
};

const getZoneLabel = (zone: string): string => {
  const zones: Record<string, string> = {
    'None': 'Zoneless',
    'DMZ': '🔵 DMZ Zone',
    'Public': '⚪ Public Zone',
    'Restricted': '🟡 Restricted Zone',
    'Core': '🔴 Core Zone',
    'Security': '🔒 Security Zone',
    'Legal': '🟣 Legal Zone',
    'Ops': '🟠 Operations Zone'
  };
  return zones[zone] || zone;
};

const getBudgetValue = (value: number): string => {
  if (value === 0) return "Neutral cost";
  if (value > 0) return `Profit center - ${Math.abs(value) * 50}/tick ROI potential`;
  return `Operating cost - ${Math.abs(value)} tick expense`;
};

const getComplianceValue = (value: number): string => {
  if (value === 0) return "Neutral impact";
  if (value > 0) return `Compliance generator - ${value}% tick improvement`;
  return `Compliance cost - ${Math.abs(value)}% tick reduction`;
};

const getThreatValue = (value: number): string => {
  if (value === 0) return "No threat impact";
  if (value > 0) return `Threat generator - ${value} tick increase`;
  return `Threat reduction - ${Math.abs(value)} tick decrease`;
};

const getROI = (config: any): string => {
  const breakEven = Math.abs(config.cost / (config.budgetGen || 1));
  const hasPositiveROI = config.budgetGen > 0 && config.cost > 0;
  if (hasPositiveROI) {
    return `ROI in ${breakEven} ticks (${Math.round(breakEven * 2)} seconds)`;
  }
  return "Support cost - necessary for operations";
};

const TOOL_GROUPS = [
  { id: 'Infra', label: 'Infra', icon: '🏗️', types: [BuildingType.None, BuildingType.Pipeline, BuildingType.Road] },
  { id: 'Gov', label: 'Gov', icon: '🏛️', types: [BuildingType.Core, BuildingType.ComplianceMgmt, BuildingType.Monitoring] },
  { id: 'Sec', label: 'Security', icon: '🛡️', types: [BuildingType.SecurityAML, BuildingType.SecurityFraud, BuildingType.Sanctions, BuildingType.SecurityIncidents] },
  { id: 'Cyber', label: 'Cyber', icon: '💀', types: [BuildingType.Internet, BuildingType.Firewall, BuildingType.SIEM, BuildingType.EDR, BuildingType.ZeroTrust] },
  { id: 'Network', label: 'Net', icon: '🌐', types: [BuildingType.LoadBalancer, BuildingType.WAF, BuildingType.Vault, BuildingType.JumpGate, BuildingType.OneWayDiode] },
  { id: 'Legal', label: 'Legal', icon: '⚖️', types: [BuildingType.LegalPrivacy, BuildingType.LegalDisputes, BuildingType.LegalMarketing] },
  { id: 'Ops', label: 'Ops', icon: '⚙️', types: [BuildingType.OpsFunding, BuildingType.OpsComplaints, BuildingType.OpsVendors] },
  { id: 'AdvSec', label: 'Adv Sec', icon: '🔐', types: [BuildingType.SOC, BuildingType.PenTestLab, BuildingType.CloudWAF, BuildingType.APIGateway, BuildingType.ThreatIntel, BuildingType.SecurityTraining] },
  { id: 'AdvComp', label: 'Adv Comp', icon: '📋', types: [BuildingType.GDPROffice, BuildingType.DataProtectionOfficer, BuildingType.InternalAudit, BuildingType.ContinuousMonitoring, BuildingType.RegulatoryRelations] },
];

const ToolButton: React.FC<{
  type: BuildingType;
  isSelected: boolean;
  onClick: () => void;
  budget: number;
  isRecommended: boolean;
  isDimmed: boolean;
}> = ({ type, isSelected, onClick, budget, isRecommended, isDimmed }) => {
  const config = BUILDINGS[type];
  const canAfford = budget >= config.cost;
  const isBulldoze = type === BuildingType.None;
  const bgColor = config.color;

  // Get enhanced tooltip content from service
  const learningObjective = tooltipContentService.getLearningObjective(type);
  const strategicValue = tooltipContentService.getStrategicValue(type);
  const realWorldApplication = tooltipContentService.getRealWorldApplication(type);
  const complianceImpact = tooltipContentService.getComplianceImpact(type);
  const progressionPath = tooltipContentService.getProgressionPath(type);
  const trainingNotes = tooltipContentService.getTrainingNotes(type);
  const awarenessPoints = tooltipContentService.getAwarenessPoints(type);

  return (
    <div className="relative group">
      {/* Enhanced Educational Tooltip with JSON Content */}
      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-96 p-5 bg-slate-900 border border-slate-600 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 group-hover:visible invisible transition-all duration-200 pointer-events-none z-50">
        {/* Header */}
        <div className="flex justify-between items-start mb-3 border-b border-slate-700 pb-3">
          <div>
            <h4 className="font-bold text-white text-lg">{config.name}</h4>
            <p className="text-xs text-slate-400 italic mt-1">{getBuildingCategory(config.category)} • Zone: {getZoneLabel(config.zone)}</p>
          </div>
          <span className={`text-xs font-mono px-3 py-1.5 rounded ${canAfford ? 'bg-emerald-900/50 text-emerald-400 border border-emerald-500/30' : 'bg-red-900/50 text-red-400 border border-red-500/30'}`}>
            {canAfford ? '✓ AFFORDABLE' : '✗ INSUFFICIENT FUNDS'}
          </span>
        </div>

        {/* Description & Purpose */}
        <div className="mb-4">
          <p className="text-sm text-slate-300 leading-relaxed mb-2">{config.description}</p>
          <div className="text-xs text-cyan-400 bg-cyan-900/20 p-2 rounded border border-cyan-800/30 italic">
            💡 <strong>Strategic Value:</strong> {strategicValue}
          </div>
        </div>

        {/* Performance Impact */}
        <div className="bg-slate-800/50 rounded-lg p-3 mb-3">
          <h5 className="text-xs font-bold text-white uppercase tracking-wider mb-2">📊 Performance Impact</h5>
          <div className="grid grid-cols-3 gap-2 text-xs">
            {config.budgetGen !== 0 && (
              <div className="text-center p-2 bg-slate-900 rounded">
                <div className="font-bold text-green-400">💰 Budget</div>
                <div className={config.budgetGen > 0 ? 'text-green-300' : 'text-red-300'}>
                  {config.budgetGen > 0 ? '+' : ''}{config.budgetGen}/tick
                </div>
                <div className="text-[9px] text-slate-500">
                  {getBudgetValue(config.budgetGen)}
                </div>
              </div>
            )}
            {config.complianceGen !== 0 && (
              <div className="text-center p-2 bg-slate-900 rounded">
                <div className="font-bold text-blue-400">📊 Compliance</div>
                <div className={config.complianceGen > 0 ? 'text-blue-300' : 'text-orange-300'}>
                  {config.complianceGen > 0 ? '+' : ''}{config.complianceGen}%/tick
                </div>
                <div className="text-[9px] text-slate-500">
                  {getComplianceValue(config.complianceGen)}
                </div>
              </div>
            )}
            {config.threatGen !== 0 && (
              <div className="text-center p-2 bg-slate-900 rounded">
                <div className="font-bold text-red-400">⚠️ Threat</div>
                <div className={config.threatGen > 0 ? 'text-red-300' : 'text-green-300'}>
                  {config.threatGen > 0 ? '+' : ''}{config.threatGen}/tick
                </div>
                <div className="text-[9px] text-slate-500">
                  {getThreatValue(config.threatGen)}
                </div>
              </div>
            )}
            {/* Neutral stats */}
            {config.budgetGen === 0 && config.complianceGen === 0 && config.threatGen === 0 && (
              <div className="text-center p-2 bg-slate-900 rounded col-span-3">
                <div className="font-bold text-slate-400">🔄 Infrastructure</div>
                <div className="text-slate-300">Support Building</div>
                <div className="text-[9px] text-slate-500">Enables connectivity</div>
              </div>
            )}
          </div>
        </div>

        {/* Enhanced Training & Awareness with JSON Content */}
        <div className="bg-blue-900/10 rounded-lg p-3 mb-3 border border-blue-800/30">
          <h5 className="text-xs font-bold text-blue-400 uppercase tracking-wider mb-2">🎓 Training & Awareness</h5>
          <div className="text-xs text-slate-300 leading-relaxed space-y-2">
            <div>• <strong>Learning Objective:</strong> {learningObjective}</div>
            <div>• <strong>Real-World Application:</strong> {realWorldApplication}</div>
            <div>• <strong>Compliance Impact:</strong> {complianceImpact}</div>
            <div>• <strong>Progression Path:</strong> {progressionPath}</div>
            <div>• <strong>Training Notes:</strong> {trainingNotes}</div>
          </div>
        </div>

        {/* Key Awareness Points */}
        <div className="bg-purple-900/10 rounded-lg p-3 mb-3 border border-purple-800/30">
          <h5 className="text-xs font-bold text-purple-400 uppercase tracking-wider mb-2">🔑 Key Awareness Points</h5>
          <div className="text-xs text-slate-300 leading-relaxed">
            {awarenessPoints.map((point, index) => (
              <div key={index} className="mb-1">• {point}</div>
            ))}
          </div>
        </div>

        {/* Current Status */}
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="p-2 bg-slate-800 rounded">
            <div className="text-slate-500 font-bold">⏱️ Building Time</div>
            <div className="text-white">1 tick (2 seconds)</div>
          </div>
          <div className="p-2 bg-slate-800 rounded">
            <div className="text-slate-500 font-bold">📈 ROI Breakdown</div>
            <div className="text-white">{getROI(config)}</div>
          </div>
        </div>

        {/* Affordability Warning */}
        {(!isBulldoze && !canAfford) && (
          <div className="text-xs text-red-400 border-t border-red-900/30 pt-3 p-2 bg-red-900/10 rounded">
            <div className="font-bold mb-1">⚠️ INSUFFICIENT FUNDS</div>
            <div>Need ${config.cost} - take loan or wait for revenue. This is blocking your growth strategy!</div>
          </div>
        )}

        {/* Success Indicator */}
        {canAfford && (
          <div className="text-xs text-emerald-400 border-t border-emerald-900/30 pt-3 p-2 bg-emerald-900/10 rounded">
            <div className="font-bold mb-1">✅ READY TO BUILD</div>
            <div>Cost: ${config.cost} • This investment will help achieve your financial goals!</div>
          </div>
        )}

        {/* Tooltip arrow */}
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
          <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-slate-600"></div>
        </div>
      </div>

      <button
        onClick={() => {
          playUiSfx('click');
          onClick();
        }}
        onMouseEnter={() => playUiSfx('hover')}
        disabled={(!isBulldoze && !canAfford)}
        className={`
          relative flex flex-col items-center justify-center rounded-xl border transition-all duration-200 flex-shrink-0 overflow-hidden
          w-20 h-16
          ${isSelected
            ? 'border-white bg-white/10 shadow-[0_0_15px_rgba(255,255,255,0.3)] z-10 scale-105'
            : 'border-white/10 bg-black/40 hover:bg-white/5 hover:border-white/30'}
          ${(!isBulldoze && !canAfford) ? 'opacity-40 grayscale cursor-not-allowed' : 'cursor-pointer'}
          ${isRecommended ? 'ring-2 ring-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.5)]' : ''}
          ${isDimmed && !isSelected && !isRecommended ? 'opacity-30 grayscale-[0.8]' : ''}
        `}
      >
        {isRecommended && (
            <div className="absolute top-0 right-0 w-3 h-3 bg-emerald-500 rounded-bl-lg z-20 animate-pulse"></div>
        )}

        <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>

        <div className="w-6 h-6 rounded-sm mb-1 shadow-inner flex items-center justify-center transition-transform group-hover:scale-110" style={{ backgroundColor: isBulldoze ? 'transparent' : bgColor }}>
          {isBulldoze && <div className="text-red-500 font-bold text-lg">✕</div>}
          {type === BuildingType.Pipeline && <div className="w-full h-1 bg-slate-900 transform -rotate-45"></div>}
          {type === BuildingType.Road && <div className="w-full h-2 bg-yellow-400"></div>}
        </div>

        <span className={`text-[10px] font-bold uppercase tracking-wide leading-none text-center px-1 w-full group-hover:text-white ${isRecommended ? 'text-emerald-300' : 'text-slate-300'}`}>
          {config.name.length > 12 ? config.name.substring(0, 10) + '..' : config.name}
        </span>

        {config.cost > 0 && (
          <span className={`text-[9px] font-mono mt-0.5 ${canAfford ? 'text-emerald-400' : 'text-red-400'}`}>
            ${config.cost}
          </span>
        )}
      </button>
    </div>
  );
};

interface ToolBarProps {
    selectedTool: BuildingType;
    onSelectTool: (t: BuildingType) => void;
    budget: number;
    unlockedBuildings: BuildingType[];
    recommendedTypes?: BuildingType[];
}

const ToolBarEnhanced: React.FC<ToolBarProps> = ({ selectedTool, onSelectTool, budget, unlockedBuildings, recommendedTypes = [] }) => {
    // Initialize with first group, or auto-detect based on selection
    const [activeTab, setActiveTab] = useState('Infra');

    // Initialize tooltip content service
    useEffect(() => {
        tooltipContentService.initialize();
    }, []);

    // Auto-switch tab if tool selected externally (e.g. from Goal click)
    useEffect(() => {
        const g = TOOL_GROUPS.find(gr => gr.types.includes(selectedTool));
        if (g) setActiveTab(g.id);
    }, [selectedTool]);

    const activeGroupData = TOOL_GROUPS.find(g => g.id === activeTab);

    // Filter tools in current group to only show unlocked ones
    const visibleTools = activeGroupData?.types.filter(t => unlockedBuildings.includes(t)) || [];

    const hasRecommendations = recommendedTypes.length > 0;

    return (
        <div className="flex flex-col gap-1 w-full md:w-auto pointer-events-auto">
            {/* Category Tabs */}
            <div className="flex gap-1 overflow-x-auto no-scrollbar pl-1">
                {TOOL_GROUPS.map(group => {
                    // Check if this group has ANY unlocked items
                    const hasUnlockedItems = group.types.some(t => unlockedBuildings.includes(t));
                    const hasRecInGroup = group.types.some(t => recommendedTypes.includes(t) && unlockedBuildings.includes(t));

                    if (!hasUnlockedItems) return null; // Hide tabs with no unlocked items

                    return (
                        <button
                            key={group.id}
                            onClick={() => {
                                playUiSfx('click');
                                setActiveTab(group.id);
                            }}
                            disabled={!hasUnlockedItems}
                            className={`
                                relative px-2 py-1 rounded-t-lg transition-all duration-200 border-b-2 flex items-center gap-1
                                ${activeTab === group.id
                                    ? 'bg-white/10 border-white text-white font-bold'
                                    : hasRecInGroup
                                        ? 'bg-emerald-900/30 border-emerald-600/50 text-emerald-300 hover:bg-emerald-900/50'
                                        : 'bg-slate-800/50 border-transparent text-slate-400 hover:bg-slate-700/50 hover:text-slate-300'}
                                ${!hasUnlockedItems ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                            `}
                        >
                            <span className="text-sm">{group.icon}</span>
                            <span className="text-xs uppercase tracking-wide font-bold hidden sm:inline">
                                {group.label}
                            </span>
                            {hasRecInGroup && (
                                <div className="absolute -top-1 -right-1 w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Tool Grid */}
            <div className="bg-black/40 backdrop-blur-sm border border-white/10 rounded-lg p-2 overflow-hidden">
                <div className="flex gap-1 flex-wrap">
                    {visibleTools.map(toolType => {
                        const isRecommended = recommendedTypes.includes(toolType);
                        const isDimmed = hasRecommendations && !isRecommended;
                        const isLocked = !unlockedBuildings.includes(toolType);

                        return (
                            <ToolButton
                                key={toolType}
                                type={toolType}
                                isSelected={selectedTool === toolType}
                                onClick={() => onSelectTool(toolType)}
                                budget={budget}
                                isRecommended={isRecommended}
                                isDimmed={isDimmed}
                            />
                        );
                    })}
                </div>

                {/* AI Recommendation Banner */}
                {hasRecommendations && (
                    <div className="mt-2 text-xs text-emerald-400 bg-emerald-900/20 rounded p-2 border border-emerald-700/30 text-center animate-pulse">
                        💡 AI Risk Officer recommends: {recommendedTypes
                            .filter(t => unlockedBuildings.includes(t))
                            .map(t => BUILDINGS[t].name)
                            .join(', ')}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ToolBarEnhanced;